import { Controller } from "@hotwired/stimulus";
import { addAction, addController, removeAction, removeController } from "./dom"; 

interface ClassMap {
    [key: string]: string;
}

export type IdentifiableController = Pick<Controller<HTMLElement>, "identifier" | "element">

export interface InjectableContext<T> {
    identifier: string;
    element: HTMLElement;
    setContext(context: T): void;
}

export interface ContextData {
    controller?: string;
    classes?: object;
}

/** Shortcut function for loading controller classes and setting up dependency injection based on a json-encoded data-context attribute` */
export function applyContext<T extends ContextData>(controller: IdentifiableController): T {
    const context = JSON.parse(controller.element.dataset.context) as T;
    if (context.classes) {
        applyClasses(controller, context.classes);
    }
    if (context.controller) {
        applyContextController(controller as InjectableContext<unknown>, context.controller);
    }
    return context;

}

/** 
 * Often the list of classes for a controller is fixed at rendering time, 
 * so rather than providing each class as a separate data attribute,
 * it makes more sense to incorporate them into a single json-encoded 'data-context' attribute.
 * For consistency with the Stimulus API, we assign the classes as properties of the controller.
 * For example, a 'placeholder' class would be assigned to the 'placeholderClass' property of the controller.
 */
export function applyClasses(controller: object, classes: object) {
    for (let controllerClass in classes) {
        controller[controllerClass + 'Class'] = classes[controllerClass];
    }
}

/**
 * A context controller is a controller that provides dependencies to another controller through event-based dependency injection.
 * Stimulus JS does not provide any type of dependency injection mechanism, so we have to implement our own.
 * To help in streamlining that process, this function puts the context controller on the same element as the dependent controller,
 * and wires up the dependency injection mechanism using a single-use event listener.
 * For this to work, the context controller must dispatch a provideContext event in the onConnect method.
 */
export function applyContextController(controller: InjectableContext<unknown>, contextControllerName: string) {
    addController(controller.element, contextControllerName);
    // This approach using the Stimulus actions API does not work because of timing issues
    // addAction(controller.element, `${contextControllerName}:provideContext->${controller.identifier}#injectContext`);
    // Simpler just to create a one-time event listener (which avoids cleanup issues)
    controller.element.addEventListener(
        `${contextControllerName}:provideContext`, 
        (event: CustomEvent) => controller.setContext(event.detail),
        { once: true }
    );
}

export function setControllerClasses(controller: IdentifiableController, classes: ClassMap) {
    for (let controllerClass in classes) {
        setControllerClass(controller, controllerClass, classes[controllerClass]);
    }
}

export function setControllerClass(controller: IdentifiableController, controllerClass: string, className: string) {
    // Note: converts the controller class from kebab-case to camelCase
    let classAttribute = `${camelize(controller.identifier)}${capitalize(camelize(controllerClass))}Class`;
    controller.element.dataset[classAttribute] = className;
}

// These functions had to be copied from stimulus.js, since they are not exported
function camelize(value: string) {
    return value.replace(/(?:[_-])([a-z0-9])/g, (_, char) => char.toUpperCase());
}

function capitalize(value: string) {
    return value.charAt(0).toUpperCase() + value.slice(1);
}

export function onControllerInit<C extends Controller>(controller: { prototype: C}, step: (this: C) => void) {
    let {prototype} = controller;
    let {initialize} = prototype;
    prototype.initialize = function(this: C) {
        initialize.call(this);
        step.call(this);
    }
    // Tried Lodash wrap function, but it did not work here
    // crontroller.prototype.initialize = wrap(controller.prototype.initialize, step); 
}

export type DisconnectableController = Pick<Controller<HTMLElement>, "disconnect">

export function onControllerDisconnect(c: DisconnectableController, step: () => any) {
    let originalFn = c.disconnect;
    c.disconnect = function(this: DisconnectableController) {
        originalFn.call(this);
        step.call(this);
    }
}

export type DisconnectableIdentifiableController = IdentifiableController & DisconnectableController;

export function addControllerActionWithCleanup(controller: DisconnectableIdentifiableController, {element, event, identifier, method}: ControllerAction) { 
    element ??= controller.element;
    identifier ??= controller.identifier;
    let action = formatAction(event, identifier, method);
    addAction(element, action);
    onControllerDisconnect(controller, () => removeAction(element, action));
}

export function addControllerAction(controller: IdentifiableController, {element, event, identifier, method}: ControllerAction): string {
    element ??= controller.element;
    identifier ??= controller.identifier;
    const action = formatAction(event, identifier, method);
    addAction(element, action);
    return action;
}

export interface ControllerAction {
    element?: HTMLElement;
    event?: string;
    identifier?: string;
    method: string;
}

function formatAction(event: string, identifier: string, method: string): string {
    let result = event ? `${event}->` : '';
    result += identifier + '#' + method;
    return result;
}
